/* eslint-disable jira/filename-case */
import React from 'react';
import memoizeOne, { type MemoizedFn } from 'memoize-one';
import type { Locale } from '@jira/platform__supported-locales/src/supported-locales.tsx';
import BoardIcon from '@atlaskit/icon/core/board';
import DashboardIcon from '@atlaskit/icon/core/dashboard';
import FilterIcon from '@atlaskit/icon/core/filter';
import PagesIcon from '@atlaskit/icon/core/pages';
import RoadmapIcon from '@atlaskit/icon/core/roadmap';
import { ContainerAvatar } from '@atlaskit/side-nav-items/container-avatar';
import { softwarePlanTimelineRoute } from '@jira/router-routes__advanced-roadmaps-routes/src/softwarePlanTimelineRoute.tsx';
import { getCollectionDetailsPageUrl } from '@jira/polaris__component-collections/src/getCollectionsPageUrl.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraProductDiscoveryCollectionAri } from '@atlassian/ari/jira-product-discovery/collection';
import { generatePath } from '@atlassian/react-resource-router';
import {
	BOARD,
	DASHBOARD,
	FILTER,
	PLAN,
	PROJECT,
	QUEUE,
	JPD_COLLECTION,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-content-recent/src/common/constants.tsx';

import type { ObjectListFlyoutHookResults } from '@atlassian/navigation-common/types';
import type { IntlShape } from '@atlassian/jira-intl';
import type { useStarredItemsPaged$data } from './__generated__/useStarredItemsPaged.graphql.ts';
import type { JiraStarredOLFItem } from './JiraRecentOLFItem.types.tsx';
import { EmojiItem } from './EmojiItem.tsx';
import PlanIcon from '@atlaskit/icon-lab/core/plan';
import { getEmojiProviderWithCustomEmoji } from '@jira/platform__atlaskit-emoji-provider/src/emoji.tsx';

type NonNullableEdges = NonNullable<useStarredItemsPaged$data['jira']['starredItems']>['edges'];
type Edge = NonNullable<NonNullableEdges>[number];
export type GQLJiraItem = NonNullable<NonNullable<Edge>['node']>;

export const getSections: MemoizedFn<
	(items: GQLJiraItem[], deps: Deps) => ObjectListFlyoutHookResults<JiraStarredOLFItem>['sections']
> = memoizeOne(getSectionItems);

function getSectionItems(
	items: GQLJiraItem[],
	deps: Deps,
): ObjectListFlyoutHookResults<JiraStarredOLFItem>['sections'] {
	return [{ items: items.map((i) => toOLFItem(i, deps)).filter(Boolean) }];
}

function toOLFItem(item: GQLJiraItem, deps: Deps): JiraStarredOLFItem | undefined {
	switch (item.__typename) {
		case 'JiraBoard': {
			if (!fg('hsbc-23_gsn-board-url-fix')) {
				// Gate off: keep the legacy boardUrl, filtering out locationless boards.
				if (item.boardUrl?.includes('locationless=true')) {
					return undefined;
				}
			} else if (item.defaultNavigationItem?.url == null) {
				return undefined;
			}

			return {
				analytics: { itemId: String(item?.boardId), itemType: BOARD },
				elemBefore: <BoardIcon spacing="spacious" color="currentColor" label="" />,
				href: item.defaultNavigationItem?.url ?? item.boardUrl ?? '',
				id: item.id,
				primaryLabel: String(item.boardTitle ?? ''),
				typename: item.__typename,
			};
		}

		case 'JiraDashboard':
			return {
				analytics: { itemId: String(item?.dashboardId), itemType: DASHBOARD },
				elemBefore: <DashboardIcon spacing="spacious" color="currentColor" label="" />,
				href: item.dashboardUrl ?? '',
				id: item.id,
				primaryLabel: String(item.dashboardTitle ?? ''),
				typename: item.__typename,
			};

		case 'JiraPlan': {
			const planId = item.planId;
			const scenarioId = item.scenario?.scenarioId ?? '';
			const planUrl = generatePath(softwarePlanTimelineRoute.path, { planId, scenarioId });

			return {
				analytics: { itemId: String(item?.planId), itemType: PLAN },
				elemBefore: <RoadmapIcon spacing="spacious" color="currentColor" label="" />,
				href: planUrl,
				id: item.id,
				primaryLabel: String(item.planTitle ?? ''),
				typename: item.__typename,
			};
		}

		case 'JiraProject':
			return {
				analytics: { itemId: String(item?.projectId), itemType: PROJECT },
				elemBefore: <ContainerAvatar src={item.avatar?.large ?? ''} />,
				href: item.defaultNavigationItem?.url ?? `/browse/${item.key}`,
				id: item.id,
				primaryLabel: item.projectTitle,
				typename: item.__typename,
			};

		case 'JiraSystemFilter':
		case 'JiraCustomFilter':
			return {
				analytics: { itemId: String(item?.filterId), itemType: FILTER },
				elemBefore: <FilterIcon spacing="spacious" color="currentColor" label="" />,
				href: item.filterUrl ?? '',
				id: item.id,
				primaryLabel: item.filterTitle,
				typename: item.__typename,
			};

		case 'JiraServiceManagementQueue':
			return {
				analytics: { itemId: String(item?.queueId), itemType: QUEUE },
				elemBefore: <PagesIcon spacing="spacious" color="currentColor" label="" />,
				href: item.queueUrl ?? '',
				id: item.id,
				primaryLabel: String(item.queueTitle ?? ''),
				typename: item.__typename,
			};
		case 'JiraProductDiscoveryCollection': {
			if (!fg('jpd-enable-roadmaps-recents-fe')) {
				return undefined;
			}
			const uuid = item.id ? JiraProductDiscoveryCollectionAri.parse(item.id).collectionId : '';
			const collectionUrl = getCollectionDetailsPageUrl(uuid);
			const fallback = <PlanIcon spacing="spacious" color="currentColor" label="" />;

			return {
				analytics: { itemId: String(item?.collectionId), itemType: JPD_COLLECTION },
				elemBefore: (
					<EmojiItem
						emoji={item.emoji}
						getEmojiProvider={getEmojiProviderWithCustomEmoji}
						cloudId={deps.cloudId}
						accountId={deps.accountId ?? undefined}
						fallback={fallback}
					/>
				),
				href: collectionUrl || item.collectionUrl || '',
				id: item.id || '',
				primaryLabel: String(item.collectionTitle ?? ''),
				typename: item.__typename,
			};
		}
		default:
			return undefined;
	}
}

type Deps = {
	formatMessage: IntlShape['formatMessage'];
	locale: Locale;
	cloudId?: string;
	accountId?: string | null;
};
